import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import {
  ArrowLeft, Pencil, Plus, Mail, Phone, MapPin, MoreHorizontal, FileText, CalendarDays,
  CreditCard, Send, CheckCircle2, Copy, Trash2, DollarSign, Wallet, ChevronDown,
} from 'lucide-react';
import { apiCustomers, apiInvoices, apiWorkorders, payments, email, invoiceActions } from '../../lib/api';
import http from '../../lib/api';
import { fmt, normalizeItems, belongsToCustomer, nextNumber, fmtDateUS, invoiceOutstanding } from '../../lib/invoiceItems';
import CustomerFormDialog from '../forms/CustomerFormDialog';
import JobFormDialog from '../forms/JobFormDialog';
import InvoiceFormDialog from '../forms/InvoiceFormDialog';
import PaymentDialog from '../forms/PaymentDialog';
import BulkBar, { TickBox } from '../ui/bulk-bar';
import useSelection from '../../lib/useSelection';
import { billJobs, setJobStatus } from '../../lib/jobBulk';
import { useJobStatuses } from '../../lib/jobStatus';
import { toast } from 'sonner';

const statusBadge = (s) => ({
  Paid: 'bg-emerald-100 text-emerald-700', Open: 'bg-blue-100 text-blue-700',
  Overdue: 'bg-red-100 text-red-700', Draft: 'bg-slate-100 text-slate-700',
  Scheduled: 'bg-slate-100 text-slate-700', 'In Progress': 'bg-blue-100 text-blue-700',
  Completed: 'bg-emerald-100 text-emerald-700', Cancelled: 'bg-red-100 text-red-700',
  Success: 'bg-emerald-100 text-emerald-700',
}[s] || 'bg-slate-100 text-slate-700');

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pays, setPays] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [allWorkorders, setAllWorkorders] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [invOpen, setInvOpen] = useState(false);
  const [editingInv, setEditingInv] = useState(null);
  const [fromWo, setFromWo] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payInv, setPayInv] = useState(null);
  const jobStatuses = useJobStatuses();

  const load = useCallback(async () => {
    try {
      const [cust, inv, wo, pay] = await Promise.all([
        apiCustomers.get(id).catch(() => null),
        apiInvoices.list().catch(() => []),
        apiWorkorders.list().catch(() => []),
        http.get('/payments').then((r) => r.data).catch(() => []),
      ]);
      setCustomer(cust);
      setAllInvoices(inv || []);
      setAllWorkorders(wo || []);
      setInvoices((inv || []).filter((x) => belongsToCustomer(x, cust)));
      setJobs((wo || []).filter((x) => belongsToCustomer(x, cust)));
      setPays((pay || []).filter((x) => belongsToCustomer(x, cust)));
    } catch (e) { /* noop */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // The balance on the customer record is a stored figure and imported accounts
  // carry 0 in it, so it read $0.00 with five unpaid invoices listed underneath.
  // The invoices on file are the record, so the figure is added up from them;
  // where the stored one disagrees it is shown rather than quietly dropped.
  const SETTLED = ['paid', 'void', 'voided', 'cancelled', 'canceled'];
  const openInv = invoices.filter(
    (i) => !SETTLED.includes(String(i.status).trim().toLowerCase()));
  const owed = Math.round(
    openInv.reduce((sum, i) => sum + invoiceOutstanding(i), 0) * 100) / 100;
  const stored = Math.round((Number(customer?.balance) || 0) * 100) / 100;
  const bal = invoices.length ? owed : stored;
  const disagrees = invoices.length > 0 && Math.abs(owed - stored) >= 0.01;

  const invSel = useSelection(invoices.map((i) => i.id));
  const jobSel = useSelection(jobs.map((j) => j.id));

  if (!customer) {
    return (
      <div className="p-8">
        <Button variant="ghost" onClick={() => navigate('/app/customers')} className="gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" />Back</Button>
        <Card className="p-10 text-center text-slate-500" data-testid="customer-not-found">Customer not found.</Card>
      </div>
    );
  }

  // ---- picking rows: invoices here, jobs on the next tab ----
  // Five invoices settled by one cheque were five trips through the row menu,
  // and a year of imported visits was one trip each to bill. Both lists use the
  // shared tick boxes, which only ever act on rows the screen is showing.
  const unpaidIds = openInv.map((i) => i.id);
  const allUnpaidPicked = unpaidIds.length > 0 && unpaidIds.every((x) => invSel.has(x));
  const pickAllUnpaid = () =>
    (allUnpaidPicked ? invSel.clear() : invSel.pickOnly(unpaidIds));
  const pickedTotal = Math.round(invoices
    .filter((i) => invSel.has(i.id))
    .reduce((sum, i) => sum + invoiceOutstanding(i), 0) * 100) / 100;

  const bulkMarkPaid = async () => {
    const ids = invSel.chosen;
    if (!ids.length) return;
    if (!window.confirm(
      `Mark ${ids.length} invoice(s) as paid, totalling ${fmt(pickedTotal)}? `
      + 'This records them as settled in full.')) return;
    try {
      const r = await invoiceActions.bulkUpdate(ids, 'Paid');
      // Keep the stored balance in step with the invoices, as marking one paid does.
      const left = Math.round(openInv
        .filter((i) => !invSel.has(i.id))
        .reduce((sum, i) => sum + invoiceOutstanding(i), 0) * 100) / 100;
      await apiCustomers.update(customer.id, { ...customer, balance: left }).catch(() => {});
      invSel.clear();
      load();
      toast.success(`${r.updated} invoice(s) marked paid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update those invoices');
    }
  };

  // Billing a batch of visits, and correcting a batch of statuses. Both live in
  // lib/jobBulk so this screen and the schedule's list say the same thing.
  const bulkInvoiceJobs = async () => {
    if (await billJobs(jobSel.chosen)) { jobSel.clear(); load(); }
  };

  const bulkJobStatus = async (status) => {
    if (await setJobStatus(jobSel.chosen, status)) { jobSel.clear(); load(); }
  };

  const markPaid = async (inv) => {
    const total = Number(inv.total) || 0;
    await apiInvoices.update(inv.id, { ...inv, status: 'Paid', paid: total, amountDue: 0 });
    await apiCustomers.update(customer.id, { ...customer, balance: Math.max(0, bal - invoiceOutstanding(inv)) }).catch(() => {});
    toast.success(`${inv.id} marked paid`);
    load();
  };
  const duplicateInv = async (inv) => {
    const num = nextNumber(allInvoices, 6000);
    const { _id, ...rest } = inv;
    await apiInvoices.create({ ...rest, id: `INV-${num}`, number: String(num), status: 'Draft', paid: 0, amountDue: Number(inv.total) || 0, date: fmtDateUS(new Date()), createdDate: fmtDateUS(new Date()) });
    toast.success(`Duplicated as INV-${num}`);
    load();
  };
  const deleteInv = async (inv) => {
    await apiInvoices.remove(inv.id);
    toast.success(`${inv.id} deleted`);
    load();
  };
  const sendInv = async (inv) => {
    const t = toast.loading(`Emailing ${inv.id}…`);
    try { const r = await email.sendInvoice(inv.id); toast.success(`Invoice ${inv.id} emailed to ${r.to}`, { id: t }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send invoice', { id: t }); }
  };
  const chargeCard = async (inv) => {
    try {
      const { url } = await payments.createSession(inv.id);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start card payment');
    }
  };
  const openInvoiceEdit = (inv) => { setEditingInv(inv); setFromWo(null); setInvOpen(true); };
  const openNewInvoice = () => { setEditingInv(null); setFromWo(null); setInvOpen(true); };
  const invoiceFromJob = (wo) => { setEditingInv(null); setFromWo(wo); setInvOpen(true); };
  const recordPayment = (inv) => { setPayInv(inv); setPayOpen(true); };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="customer-detail-page">
      <Button variant="ghost" onClick={() => navigate('/app/customers')} className="gap-1.5 -ml-2" data-testid="customer-back-btn"><ArrowLeft className="w-4 h-4" />Customers</Button>

      <div className="flex flex-wrap justify-between gap-4 items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{customer.name}</h1>
            <Badge className={`${statusBadge(customer.status)} border-0`}>{customer.status || 'Active'}</Badge>
            <Badge className="bg-slate-100 text-slate-700 border-0">{customer.type || 'Residential'}</Badge>
          </div>
          <div className="text-slate-500 mt-1 text-sm font-mono">{customer.accountNumber ? `Account #${customer.accountNumber}` : customer.id}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)} className="gap-1.5" data-testid="edit-customer-btn"><Pencil className="w-4 h-4" />Edit Customer</Button>
          <Button variant="outline" onClick={() => { setEditingJob(null); setJobOpen(true); }} className="gap-1.5" data-testid="new-job-btn"><CalendarDays className="w-4 h-4" />New Job</Button>
          <Button onClick={openNewInvoice} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-invoice-btn"><Plus className="w-4 h-4" />New Invoice</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200 md:col-span-2">
          <h3 className="font-bold text-slate-900 mb-3">Contact</h3>
          <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm text-slate-700">
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{customer.email || '—'}</div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{customer.phone || '—'}</div>
            <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="w-4 h-4 text-slate-400" />{[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ') || '—'}</div>
            {customer.company && <div className="flex items-center gap-2 sm:col-span-2 text-slate-500">{customer.company}</div>}
          </div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Wallet className="w-4 h-4" />Open Balance</div>
          <div className={`text-3xl font-extrabold mt-1 tabular-nums ${bal > 0 ? 'text-red-600' : 'text-slate-900'}`} data-testid="customer-balance">{fmt(bal)}</div>
          <div className="text-xs text-slate-500 mt-1">{openInv.length} open invoice(s)</div>
          {disagrees && (
            <div className="text-xs text-amber-600 mt-1" data-testid="customer-balance-mismatch">
              Account record says {fmt(stored)}
            </div>
          )}
        </Card>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="invoices" className="gap-1.5" data-testid="tab-invoices"><FileText className="w-4 h-4" />Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="jobs" className="gap-1.5" data-testid="tab-jobs"><CalendarDays className="w-4 h-4" />Jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="payments" className="gap-1.5" data-testid="tab-payments"><DollarSign className="w-4 h-4" />Payments ({pays.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Card className="border-slate-200 overflow-x-auto">
            <BulkBar count={invSel.count} onClear={invSel.clear} note={fmt(pickedTotal)}
                     testId="detail-bulk-bar" countTestId="detail-bulk-count">
              <Button size="sm" onClick={bulkMarkPaid} className="h-8 gap-1 bg-[#8BC53F] hover:bg-[#6FA82C] text-white" data-testid="detail-bulk-paid-btn">
                <CheckCircle2 className="w-3.5 h-3.5" />Mark as paid
              </Button>
            </BulkBar>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 text-left w-10">
                    <TickBox checked={allUnpaidPicked} onChange={pickAllUnpaid}
                             label="Select every unpaid invoice" testId="detail-select-all-unpaid"/>
                  </th>
                  <th className="px-5 py-3 text-left">Invoice</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Due</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.map((inv) => (
                  <tr key={inv.id} className={`hover:bg-slate-50 ${invSel.has(inv.id) ? 'bg-[#8BC53F]/5' : ''}`} data-testid={`detail-invoice-row-${inv.id}`}>
                    <td className="px-5 py-3">
                      <TickBox checked={invSel.has(inv.id)} onChange={() => invSel.toggle(inv.id)}
                               label={`Select invoice ${inv.id}`} testId={`detail-select-invoice-${inv.id}`}/>
                    </td>
                    <td className="px-5 py-3"><button onClick={() => openInvoiceEdit(inv)} className="font-semibold text-[#6FA82C] hover:underline">{inv.id}</button></td>
                    <td className="px-5 py-3 text-slate-600">{inv.date}</td>
                    <td className="px-5 py-3 text-slate-600">{inv.due}</td>
                    <td className="px-5 py-3"><Badge className={`${statusBadge(inv.status)} border-0`}>{inv.status}</Badge></td>
                    <td className="px-5 py-3 text-right font-bold tabular-nums">{fmt(inv.total)}</td>
                    <td className="px-5 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button className="text-slate-400 hover:text-slate-700 p-1" data-testid={`invoice-menu-${inv.id}`}><MoreHorizontal className="w-4 h-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => openInvoiceEdit(inv)}><Pencil className="w-4 h-4 mr-2" />Edit invoice</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendInv(inv)}><Send className="w-4 h-4 mr-2" />Send / Resend</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => recordPayment(inv)}><DollarSign className="w-4 h-4 mr-2" />Record payment</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => chargeCard(inv)}><CreditCard className="w-4 h-4 mr-2" />Charge card</DropdownMenuItem>
                          {String(inv.status).toLowerCase() !== 'paid' && <DropdownMenuItem onClick={() => markPaid(inv)}><CheckCircle2 className="w-4 h-4 mr-2" />Mark as paid</DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => duplicateInv(inv)}><Copy className="w-4 h-4 mr-2" />Duplicate</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => deleteInv(inv)} className="text-red-600 focus:text-red-700"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No invoices yet. <button onClick={openNewInvoice} className="text-[#6FA82C] font-semibold hover:underline">Create one</button>.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="jobs" className="mt-4">
          <Card className="border-slate-200 overflow-x-auto">
            <BulkBar count={jobSel.count} onClear={jobSel.clear}
                     testId="detail-job-bulk-bar" countTestId="detail-job-bulk-count">
              <Button size="sm" onClick={bulkInvoiceJobs} className="h-8 gap-1 bg-[#8BC53F] hover:bg-[#6FA82C] text-white" data-testid="detail-job-bulk-invoice-btn">
                <FileText className="w-3.5 h-3.5" />Create invoices
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-8 gap-1 bg-white" data-testid="detail-job-bulk-status-btn">
                    Change status <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                  {jobStatuses.map((st) => (
                    <DropdownMenuItem key={st.value} onClick={() => bulkJobStatus(st.value)}
                                      data-testid={`detail-job-bulk-status-${st.value}`}>
                      Mark as {st.value}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BulkBar>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 text-left w-10">
                    <TickBox checked={jobSel.allPicked} onChange={jobSel.toggleAll}
                             label="Select every job listed" testId="detail-select-all-jobs"/>
                  </th>
                  <th className="px-5 py-3 text-left">Job</th><th className="px-5 py-3 text-left">Service</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Tech</th><th className="px-5 py-3 text-left">Status</th><th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {jobs.map((wo) => (
                  <tr key={wo.id} className={`hover:bg-slate-50 ${jobSel.has(wo.id) ? 'bg-[#8BC53F]/5' : ''}`} data-testid={`detail-job-row-${wo.id}`}>
                    <td className="px-5 py-3">
                      <TickBox checked={jobSel.has(wo.id)} onChange={() => jobSel.toggle(wo.id)}
                               label={`Select job ${wo.workOrderNumber || wo.id}`} testId={`detail-select-job-${wo.id}`}/>
                    </td>
                    <td className="px-5 py-3"><button onClick={() => { setEditingJob(wo); setJobOpen(true); }} className="font-mono text-[#6FA82C] hover:underline" data-testid={`open-job-${wo.id}`}>{wo.workOrderNumber ? `#${wo.workOrderNumber}` : wo.id}</button></td>
                    <td className="px-5 py-3 font-medium text-slate-900">{wo.service || wo.name}{wo.recurrence ? <span className="ml-2 text-[10px] uppercase tracking-wide text-[#6FA82C] font-bold">{wo.recurrence}</span> : null}</td>
                    <td className="px-5 py-3 text-slate-600">{wo.date}</td>
                    <td className="px-5 py-3 text-slate-600">{wo.tech || '—'}</td>
                    <td className="px-5 py-3"><Badge className={`${statusBadge(wo.status)} border-0`}>{wo.status}</Badge></td>
                    <td className="px-5 py-3 text-right flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setEditingJob(wo); setJobOpen(true); }} className="h-7 gap-1 text-xs" data-testid={`edit-job-${wo.id}`}><Pencil className="w-3.5 h-3.5" />Open</Button>
                      <Button size="sm" variant="outline" onClick={() => invoiceFromJob(wo)} className="h-7 gap-1 text-xs" data-testid={`invoice-from-job-${wo.id}`}><FileText className="w-3.5 h-3.5" />Invoice</Button>
                    </td>
                  </tr>
                ))}
                {jobs.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-500">No jobs yet. <button onClick={() => setJobOpen(true)} className="text-[#6FA82C] font-semibold hover:underline">Schedule one</button>.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <Card className="border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
                <tr><th className="px-5 py-3 text-left">Payment</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Method</th><th className="px-5 py-3 text-left">Invoice</th><th className="px-5 py-3 text-right">Amount</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pays.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 font-mono text-slate-600">{p.id}</td>
                    <td className="px-5 py-3 text-slate-600">{p.date}</td>
                    <td className="px-5 py-3 capitalize text-slate-600">{p.method}</td>
                    <td className="px-5 py-3 text-slate-600">{p.invoiceNumber || p.invoiceId || '—'}</td>
                    <td className="px-5 py-3 text-right font-bold text-emerald-600 tabular-nums">{fmt(p.amount)}</td>
                  </tr>
                ))}
                {pays.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">No payments recorded.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <CustomerFormDialog open={editOpen} setOpen={setEditOpen} editing={customer} onSaved={load} />
      <JobFormDialog open={jobOpen} setOpen={(v) => { setJobOpen(v); if (!v) setEditingJob(null); }} customer={customer} editing={editingJob} allWorkorders={allWorkorders} onSaved={load} />
      <InvoiceFormDialog open={invOpen} setOpen={setInvOpen} editing={editingInv} fixedCustomer={customer} fromWorkorder={fromWo} allInvoices={allInvoices} onSaved={load} />
      <PaymentDialog open={payOpen} setOpen={setPayOpen} invoice={payInv} customer={customer} onSaved={load} />
    </div>
  );
}
